/*
*   File : redeem.js
*   Author : https://evoqins.com
*   Description : Portfolio redeem page
*   Integrations : NA
*   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Modal } from 'bootstrap';

//************ Components  **************// 
import { PrimaryButton, TextButton } from '../../Component/Buttons';
import { Chip, CustomCheckBox, Loader } from '../../Component/Others';
import { _createAndUpdatePortFolio, _executeBrokerOrder, _formattedAmount, _generateProductCode, _openBrokerOrderPlatform, _scrollToTop, _validateBrokerOrder } from '../../Helper';
import { CustomAmountInput } from '../../Component/FormElements';
import { OrderStatusModal } from '../../Component/Modal';
import { Icon } from '../../Component/Icon';

//************ Styles  **************// 
import style from "../../Styles/Component/card.module.scss";

const RedeemInvestment = () => {

    const USER_DATA = useSelector((store) => store.Reducer.USER_DATA);
    const navigate = useNavigate();
    const { state } = useLocation();
    const orderStatusData = useRef();

    let broker_data = JSON.parse(localStorage.getItem("indvestment_connected_broker"));
    let product_data = (state && state.portfolio_data) || {};
    let basket_data = (state && state.basket_data) || {};
    let holdings = (state && state.holdings_data) || [];

    const [redeemAmt, setRedeemAmt] = useState(product_data && product_data.current_value);
    const [redeemAmtErr, setRedeemAmtErr] = useState('');
    const [redeemLoader, setRedeemLoader] = useState(false);
    const [showOrderStatusModal, setShowOrderStatusModal] = useState(false);
    const [redeemAll, setRedeemAll] = useState(true);
    const [pageLoader, setPageLoader] = useState(true);


    useEffect(() => {
        // Scroll the page to top
        _scrollToTop();

        if (state === null) {
            navigate("/portfolio");
        } else {
            setPageLoader(false);
        }
    }, []);

    useEffect(() => {
        if (showOrderStatusModal) {
            const order_status_modal = new Modal(document.getElementById('order-status-modal'));
            order_status_modal.show();
        }
    }, [showOrderStatusModal]);

    // handle back
    const _handleBack = () => {
        navigate(-1);
    };

    // handle navigate to creator detail page
    const _handleNavigateToCreatorDetail = () => {
        if (product_data.creator_id) {
            navigate("/explore/creator-detail", {
                state: {
                    creator_id: Number(product_data.creator_id)
                }
            });
        }
    };


    // handle change redeem amt
    const _handleChangeRedeemAmount = (value) => {
        setRedeemAmt(value);
        setRedeemAmtErr("");
    };

    // handle select redeem all
    const _handleSelectRedeemAll = () => {
        setRedeemAll(prev => !prev);
    };

    // handle redeem invest
    const _handleRedeemInvest = () => {
        setRedeemLoader(true);
        // API call
        _callGenerateProductCode();

    };

    // handle close order status modal
    const _handleCloseOrderStatusModal = () => {
        setShowOrderStatusModal(false);
    };

    // handle navigate to portfolio page
    const _handleNavigatePortfolio = () => {
        navigate("/portfolio");
        setShowOrderStatusModal(false);
    };

    // handle navigate to order detail page
    const _handleNavigateOrderDetail = (portfolio_id) => {
        navigate("/orders/detail", {
            state: {
                portfolio_id
            }
        });
        setShowOrderStatusModal(false);
    };



    // ===================================== //
    // ============ API CALLS ============== //


    // handle call get Product code
    const _callGenerateProductCode = async () => {

        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            holdings: holdings,
            action: "SELL",
            portfolio_id: state.portfolio_id,
            user_id: USER_DATA.unique_identifier,
            operation: state.operation
        };

        const response = await _generateProductCode(params);

        if (response.status_code === 200) {
            if (broker_data.publisher) {
                _callOpenBrokerOrderPlatform(response.data.product_code, response.data.portfolio_action_id);
            } else {
                // Call execute API - Manual
                _callExecuteBrokerOrder(response.data.product_code, response.data.portfolio_action_id);
            }
        } else {
            toast.dismiss();
            toast.error(response.message);
            setRedeemLoader(false);
        }

    };

    // handle call open broker order platform
    const _callOpenBrokerOrderPlatform = async (product_code, portfolio_action_id) => {

        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            holdings: holdings,
            transaction_type: "SELL",
            portfolio_id: state.portfolio_id,
            product_code: product_code,
            user_id: USER_DATA.unique_identifier,
        };

        const response = await _openBrokerOrderPlatform(params);

        if (response.status_code === 200) {
            // Open broker order window
            const broker_window_url = URL.createObjectURL(
                new Blob([response.result], { type: "text/html" })
            );
            // Broker order creation window
            const broker_order_window = window.open(broker_window_url, "_blank");
            let current_url = "";
            let interval = setInterval(() => {
                try {
                    current_url = broker_order_window.location.href;
                    let url_data = current_url.split('?');
                    if (url_data.length !== 1) {
                        // Taking the type and action from the URL
                        let params = new URLSearchParams(url_data[1]);
                        let access_token = params.get("request_token");
                        const type = params.get("type");
                        const action = params.get("action");
                        const status = params.get("status");
                        if (type === "basket" && action === "basket" && access_token && status === "success") {
                            clearInterval(interval);
                            // API call
                            _callValidateBrokerOrder(access_token, product_code, portfolio_action_id);
                            broker_order_window.close();
                        } else if (status === "cancelled") {
                            clearInterval(interval);
                            broker_order_window.close();
                            toast.dismiss();
                            toast.error("Redeem has been cancelled");
                            setRedeemLoader(false);
                        }
                    }
                } catch (error) {
                    console.log("Cross-origin access error. Waiting for the window to navigate...");
                }

                if (broker_order_window.closed) {
                    clearInterval(interval);
                    if (current_url.length === 0) {
                        setRedeemLoader(false);
                    }
                };

            }, 1000);

        } else {
            toast.dismiss();
            toast.error(response.message);
            setRedeemLoader(false);
        }

    };

    // handle call execute Broker Order 
    const _callExecuteBrokerOrder = async (product_code, portfolio_action_id) => {
        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            holdings: holdings.map(holding => {
                return {
                    ...holding,
                    action: "SELL",
                }
            }),
            product_code: product_code,
            user_id: USER_DATA.unique_identifier,
        };

        const response = await _executeBrokerOrder(params);
        if (response.status_code === 200) {
            // API 
            _callUpdatePortFolio(product_code, portfolio_action_id);
        } else {
            toast.dismiss();
            toast.error(response.message);
            setRedeemLoader(false);
        };

    };

    // handle call validate broker order
    const _callValidateBrokerOrder = async (access_token, product_code, portfolio_action_id) => {
        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            user_id: USER_DATA.unique_identifier,
            creator_id: basket_data.creator_info.creator_id,
            access_token: access_token,
        };

        const response = await _validateBrokerOrder(params);

        if (response.status_code === 200) {
            await _callUpdatePortFolio(product_code, portfolio_action_id);
        } else {
            toast.dismiss();
            toast.error(response.message);
            setRedeemLoader(false);
        }
    };

    // handle call update portfolio
    const _callUpdatePortFolio = async (product_code, portfolio_action_id) => {
        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            user_id: USER_DATA.unique_identifier,
            product_code: product_code,
            portfolio_id: state.portfolio_id,
            investment_amount: product_data.investment_value,
            operation: state.operation,
            portfolio_action_id: portfolio_action_id
        };

        const response = await _createAndUpdatePortFolio(params);

        if (response.status_code === 200 || response.status_code === 202 || response.status_code === 204) {
            // Redemption status  = success, partial, awaiting confirmation 
            setShowOrderStatusModal(true);
            orderStatusData.current = {
                type: 1,
                status_message: response.message,
                portfolio_id: response.data.portfolio_id
            };
        } else if (response.status_code === 205) {
            // Order rejected
            setShowOrderStatusModal(true);
            orderStatusData.current = {
                type: 2,
                status_message: response.message,
            };
        } else if (response.status_code === 400) {
            // Order awaiting confirmation
            setShowOrderStatusModal(true);
            orderStatusData.current = {
                type: 2,
                status_message: response.message,
                status_code: response.status_code
            };
        } else {
            // Something went wrong
            setShowOrderStatusModal(true);
            orderStatusData.current = {
                type: 2,
                status_message: response.message,
                status_code: response.status_code
            };
        }
        setRedeemLoader(false);
    };

    return (
        <>
            {
                pageLoader === false ?

                    <div className="min-h-100vh pb-5">
                        <TextButton name="Redeem"
                            type={2}
                            icon={true}
                            onPress={_handleBack} />
                        <div className="row mt-4 flex-column flex-md-row gap-4 gap-md-0">
                            <div className="col-12 col-md-7">
                                {/* Product info card */}
                                <div className="">
                                    <div className={`${style.e_alphabasket_card_wrapper} cursor-pointer h-100`}>
                                        <div className='e-bg-white border-radius-16 h-100'>
                                            <div data-type={product_data.strategy_type === "alpha-basket" ? "basket" : "basket-pro"}
                                                className={`${style.e_heading_wrapper} position-relative`}>
                                                <img src={require(product_data.strategy_type === "alpha-basket" ? "../../Assets/Images/Card/alphabasket-tag.svg" : "../../Assets/Images/Card/alphabasket-pro-tag.svg").default}
                                                    draggable={false}
                                                    className='position-absolute left--6px top-8px h-28'
                                                    alt='tag' />
                                                <Chip label={product_data.risk_category_info.risk_category_name}
                                                    className={product_data.risk_category_info.risk_category_name === 'Conservative' ? 'e-text-deep-green e-bg-nyanza e-border-1px-light-green' :
                                                        product_data.risk_category_info.risk_category_name === 'Moderate' ? 'e-text-brown e-bg-lemon-chiffon e-border-1px-jasmine' :
                                                            'e-text-cadmium-purple e-bg-lavender-blush e-border-1px-light-red'} />
                                            </div>
                                            <div className='p-3'>
                                                <div className='d-flex align-items-center gap-12px e-border-bottom-1px-anti-flash-white pb-3'>
                                                    <img src={product_data.strategy_icon}
                                                        draggable={false}
                                                        className='border-radius-4 bg-white p-1'
                                                        width={48}
                                                        alt={product_data.strategy_name} />
                                                    <div>
                                                        <p className='e-font-14-res mb-1 e-text-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px'>
                                                            {product_data.strategy_name}
                                                        </p>
                                                        {
                                                            product_data.creator_name &&
                                                            <span className='e-font-12-res mb-0 e-text-philippine-gray e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px text-decoration-hover-underline'
                                                                onClick={_handleNavigateToCreatorDetail}>
                                                                by {product_data.creator_name || "--"}
                                                            </span>
                                                        }
                                                    </div>
                                                </div>

                                                {/* basket details */}
                                                <div className='pt-2'>
                                                    <div className='row justify-content-between'>
                                                        <div className='col-6'>
                                                            <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                Invested Amount
                                                            </p>
                                                            <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                {_formattedAmount(product_data.investment_value)}
                                                            </p>
                                                        </div>
                                                        <div className='col-6 text-end'>
                                                            <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                                                Total Amount Available
                                                            </p>
                                                            <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                                                {_formattedAmount(product_data.current_value)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Stock Breakdown */}
                                <div className="mt-4">
                                    <h6 className='mb-2 e-primary-font-clash-display e-text-chinese-black e-font-16 e-font-weight-500 line-height-24px'>
                                        Basket Constituents
                                    </h6>

                                    <div className='e-border-1px-anti-flash-white border-radius-8'>
                                        <div className='e-bg-magnolia px-3 padding-10px-tb'>
                                            <div className='row flex-nowrap justify-content-between'>
                                                <div className='col-8'>
                                                    <h6 className='mb-0 e-text-black e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px'>
                                                        {holdings.length === 1 ? "Stock" : "Stocks"} ({holdings.length})
                                                    </h6>
                                                </div>
                                                <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                    <h6 className='text-nowrap text-sm-wrap mb-0 e-text-black e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px cursor-pointer'>
                                                        Amount
                                                    </h6>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Added stocks */}
                                        <div className='px-3'>
                                            {
                                                holdings.map((item, index) => (
                                                    <div className='row flex-nowrap e-border-bottom-1px-anti-flash-white py-2 align-items-center justify-content-between'
                                                        key={index}>
                                                        <div className='col-8'>
                                                            <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-400 line-height-20px'>
                                                                {item.holding_name}
                                                            </p>
                                                            <p className='mb-1 e-text-slate-gray e-alt-font-lato e-font-14 e-font-weight-400 line-height-20px'>
                                                                {item.nse_exchange_symbol || item.bse_exchange_symbol}
                                                            </p>

                                                            <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-12 e-font-weight-400 line-height-20px'>
                                                                Quantity: <span className='e-text-primary-color e-font-weight-600'>{item.quantity}</span>
                                                            </p>
                                                        </div>

                                                        <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                            <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                                {_formattedAmount(Number(item.current_value))}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>

                                        {/* Total Amount */}
                                        <div className='px-3 padding-18px-tb'>
                                            <div className='row flex-nowrap justify-content-between'>
                                                <div className='col-8'>
                                                    <h6 className='e-font-14-res  mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                        Total Amount
                                                    </h6>
                                                </div>
                                                <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                    <h6 className='e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-24px'>
                                                        {_formattedAmount(Number(holdings.reduce((initial, item) => initial + Number(item.current_value), 0)))}
                                                    </h6>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Redeem Card with input */}
                            <div className="col-12 col-md-5">
                                <div className="e-gradient-bg-primary e-border-1px-lavender-light border-radius-16 p-3">
                                    <TextButton name="Verify & Redeem"
                                        className="mb-3"
                                        type={2}
                                        icon={false} />

                                    <span className='e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px'>
                                        Date
                                        <span className='ps-1 e-alt-font-lato e-font-weight-600 line-height-20px'>
                                            {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).replace(',', '')}
                                        </span>
                                    </span>

                                    {/* <div className="my-3"> */}
                                    <div className="mt-3 mb-4">
                                        <CustomAmountInput
                                            // label="Enter amount"
                                            readOnly={redeemAll ? true : false}
                                            placeholder="Enter amount"
                                            require
                                            prefix="₹"
                                            padding="padding-14px-tb ps-4"
                                            value={redeemAmt}
                                            error={redeemAmtErr}
                                            className="w-100 bg-transparent"
                                            handleChange={_handleChangeRedeemAmount} />

                                        {/* Redeem All checkbox */}
                                        <CustomCheckBox label="Redeem all"
                                            className="pt-3 d-none"
                                            checked={redeemAll}
                                            handleCheck={_handleSelectRedeemAll}
                                        />
                                    </div>

                                    <div className="mt-4 d-flex gap-2 py-2 padding-12px-lr e-bg-magnolia e-border-0-5px-pale-lavender border-radius-8">
                                        <Icon icon={"note-icon"}
                                            className="d-flex w-18px" />
                                        <p className="mb-0 e-text-chinese-black e-alt-font-lato e-font-weight-400 e-font-14 line-height-18px">
                                            If required by your broker, please authenticate T-PIN before placing an order
                                        </p>
                                    </div>

                                    <PrimaryButton name="Redeem Now"
                                        padding="padding-18px-tb e-padding-8px-tb-res"
                                        fontSize="e-font-14-res e-font-16"
                                        className="w-100 h-54 mt-3"
                                        status={redeemLoader}
                                        disabled={redeemLoader}
                                        onPress={_handleRedeemInvest} />

                                </div>
                            </div>
                        </div>

                        {/* Order status modal */}
                        {
                            showOrderStatusModal &&
                            <OrderStatusModal action="REDEEM"
                                type={orderStatusData.current.type}
                                statusMessage={orderStatusData.current.status_message}
                                portfolioId={orderStatusData.current.portfolio_id}
                                handleClose={_handleCloseOrderStatusModal}
                                handleNavigatePortfolio={_handleNavigatePortfolio}
                                handleNavigateOrderDetail={_handleNavigateOrderDetail} />
                        }


                    </div>
                    :
                    <Loader wrapperClassName="min-vh-100 margin-200px-top" />
            }
        </>
    )
};

export default RedeemInvestment;
